import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Plus, Trash2 } from "lucide-react";
import { rupiah, tanggal } from "@/lib/format";

export default function RatesPage() {
  const { data: rates, reload } = useFetch("/contract-rates");
  const { data: contracts } = useFetch("/contracts");
  const { data: materials } = useFetch("/materials");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ rate_basis: "daily", status: "Aktif" });
  const [saving, setSaving] = useState(false);

  const matName = (id) => (materials || []).find((m) => m.id === id)?.name || id;
  const conName = (id) => (contracts || []).find((c) => c.id === id)?.number || id;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.contract_id || !form.material_id || !form.rate) { toast.error("Kontrak, material, dan tarif wajib diisi"); return; }
    const con = (contracts || []).find((c) => c.id === form.contract_id);
    setSaving(true);
    try {
      await api.post("/contract-rates", {
        ...form, rate: Number(form.rate), client_id: con?.client_id, project_id: con?.project_id,
      });
      toast.success("Tarif ditambahkan");
      setOpen(false); setForm({ rate_basis: "daily", status: "Aktif" }); reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan tarif");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Nonaktifkan tarif ini?")) return;
    await api.delete(`/contract-rates/${id}`);
    toast.success("Tarif dinonaktifkan"); reload();
  };

  return (
    <div>
      <PageHeader title="Tarif Material" subtitle="Harga per klien/proyek/kontrak/material (versioning, cegah tumpang tindih)"
        action={<Button onClick={() => setOpen(true)} data-testid="rates-add-btn" className="bg-[#9333EA] hover:bg-[#7e22ce]"><Plus className="h-4 w-4 mr-1" /> Tambah Tarif</Button>} />
      <DataTable testId="rates" data={rates || []} columns={[
        { key: "contract_id", label: "Kontrak", render: (r) => conName(r.contract_id) },
        { key: "material_id", label: "Material", render: (r) => matName(r.material_id) },
        { key: "rate", label: "Tarif", numeric: true, render: (r) => rupiah(r.rate) },
        { key: "rate_basis", label: "Dasar", render: (r) => ({ daily: "unit/hari", weekly: "unit/minggu", monthly: "unit/bulan" }[r.rate_basis]) },
        { key: "valid_from", label: "Berlaku", render: (r) => `${tanggal(r.valid_from)} – ${tanggal(r.valid_to)}` },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
        { key: "act", label: "", sortable: false, render: (r) => <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); del(r.id); }} data-testid={`rate-del-${r.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button> },
      ]} />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto scrollbar-thin">
          <SheetHeader><SheetTitle className="font-display">Tambah Tarif</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <Field label="Kontrak"><Select value={form.contract_id || ""} onValueChange={(v) => setF("contract_id", v)}><SelectTrigger data-testid="rate-field-contract"><SelectValue placeholder="Pilih kontrak" /></SelectTrigger><SelectContent>{(contracts || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.number}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Material"><Select value={form.material_id || ""} onValueChange={(v) => setF("material_id", v)}><SelectTrigger data-testid="rate-field-material"><SelectValue placeholder="Pilih material" /></SelectTrigger><SelectContent>{(materials || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.code} · {m.name}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Nilai Tarif (Rp)"><Input type="number" data-testid="rate-field-rate" value={form.rate || ""} onChange={(e) => setF("rate", e.target.value)} /></Field>
            <Field label="Dasar Tarif"><Select value={form.rate_basis} onValueChange={(v) => setF("rate_basis", v)}><SelectTrigger data-testid="rate-field-basis"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="daily">unit/hari</SelectItem><SelectItem value="weekly">unit/minggu</SelectItem><SelectItem value="monthly">unit/bulan</SelectItem></SelectContent></Select></Field>
            <Field label="Mulai Berlaku"><Input type="date" data-testid="rate-field-from" value={form.valid_from || ""} onChange={(e) => setF("valid_from", e.target.value)} /></Field>
            <Field label="Akhir Berlaku"><Input type="date" data-testid="rate-field-to" value={form.valid_to || ""} onChange={(e) => setF("valid_to", e.target.value)} /></Field>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="rates-save-btn" className="bg-[#9333EA] hover:bg-[#7e22ce]">{saving ? "Menyimpan..." : "Simpan"}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>;
}
